/**
 * Form Schema & Validation System
 * ===============================
 * Schema-driven form validation inspired by the backend Python system.
 * Provides field-level and form-level validation with comprehensive error reporting.
 */

import { runValidator } from './validators';
import { sanitizeByType } from './sanitizers';

export type FieldType =
  | 'email'
  | 'phone'
  | 'url'
  | 'uuid'
  | 'integer'
  | 'boolean'
  | 'title'
  | 'text'
  | 'body'
  | 'dropdown'
  | 'file'
  | 'date'
  | 'iso_datetime';

export interface FieldValidatorOptions {
  minLength?: number;
  maxLength?: number;
  minValue?: number;
  maxValue?: number;
  allowHtml?: boolean;
  choices?: any[];
  maxFileSize?: number;
  allowedExtensions?: string[];
  customValidator?: (value: any) => { isValid: boolean; error?: string; sanitized?: any };
  dateFormat?: string;
}

/**
 * FormField: Represents a single field in a form schema
 */
export class FormField {
  name: string;
  fieldType: FieldType;
  required: boolean;
  label: string;
  options: FieldValidatorOptions;

  constructor(
    name: string,
    fieldType: FieldType,
    required: boolean = false,
    label?: string,
    options: FieldValidatorOptions = {}
  ) {
    this.name = name;
    this.fieldType = fieldType;
    this.required = required;
    this.label = label || name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    this.options = options;
  }

  /**
   * Validate a field value and return result with sanitized value
   */
  validate(value: any): { isValid: boolean; error?: string; sanitized?: any } {
    const isEmpty =
      value === null ||
      value === undefined ||
      (typeof value === 'string' && !value.trim()) ||
      (Array.isArray(value) && value.length === 0);

    // Check if required
    if (this.required && isEmpty) {
      return {
        isValid: false,
        error: `${this.label} is required`,
      };
    }

    // Allow empty for optional fields
    if (!this.required && isEmpty) {
      return {
        isValid: true,
        sanitized: undefined,
      };
    }

    // Run custom validator if provided
    if (this.options.customValidator) {
      return this.options.customValidator(value);
    }

    // Run type-specific validator
    const validationResult = runValidator(value, this.fieldType, {
      min: this.options.minValue,
      max: this.options.maxValue,
      minLength: this.options.minLength,
      maxLength: this.options.maxLength,
      choices: this.options.choices,
      maxSize: this.options.maxFileSize,
      extensions: this.options.allowedExtensions,
      format: this.options.dateFormat,
      allowHtml: this.options.allowHtml,
    });

    if (!validationResult.isValid) {
      return {
        isValid: false,
        error: validationResult.error,
      };
    }

    // Sanitize the value
    const sanitized = sanitizeByType(value, this.fieldType, {
      maxLength: this.options.maxLength,
      allowHtml: this.options.allowHtml,
    });

    return {
      isValid: true,
      sanitized,
    };
  }
}

/**
 * FormSchema: Defines the structure and validation rules for a form
 */
export class FormSchema {
  name: string;
  fields: Map<string, FormField>;

  constructor(name: string, fields: FormField[] = []) {
    this.name = name;
    this.fields = new Map(fields.map(field => [field.name, field]));
  }

  /**
   * Static factory method to create a schema
   */
  static create(name: string, fieldDefinitions: Record<string, FormField>): FormSchema {
    const fields = Object.values(fieldDefinitions);
    return new FormSchema(name, fields);
  }

  /**
   * Add a field to the schema
   */
  addField(field: FormField): void {
    this.fields.set(field.name, field);
  }

  /**
   * Get a field by name
   */
  getField(fieldName: string): FormField | undefined {
    return this.fields.get(fieldName);
  }

  /**
   * Get all field names
   */
  getFieldNames(): string[] {
    return Array.from(this.fields.keys());
  }

  /**
   * Get all fields
   */
  getAllFields(): FormField[] {
    return Array.from(this.fields.values());
  }
}

/**
 * FormValidator: Validates complete form data against a schema
 */
export class FormValidator {
  schema: FormSchema;
  errors: Map<string, string[]> = new Map();
  sanitizedData: Record<string, any> = {};

  constructor(schema: FormSchema) {
    this.schema = schema;
  }

  /**
   * Validate complete form data
   */
  validate(
    formData: Record<string, any>,
    strict: boolean = false
  ): {
    isValid: boolean;
    errors: Record<string, string[]>;
    sanitizedData: Record<string, any>;
  } {
    this.errors.clear();
    this.sanitizedData = {};

    // Check for unknown fields if strict mode
    if (strict) {
      const unknownFields = Object.keys(formData).filter(
        key => !this.schema.getFieldNames().includes(key)
      );
      if (unknownFields.length > 0) {
        this.addError('_form', `Unknown fields: ${unknownFields.join(', ')}`);
      }
    }

    // Validate each field in schema
    for (const field of this.schema.getAllFields()) {
      const value = formData[field.name];
      const result = field.validate(value);

      if (!result.isValid) {
        this.addError(field.name, result.error || 'Invalid value');
      } else {
        this.sanitizedData[field.name] = result.sanitized;
      }
    }

    const isValid = this.errors.size === 0;

    return {
      isValid,
      errors: this.getErrorsAsObject(),
      sanitizedData: this.sanitizedData,
    };
  }

  /**
   * Add a validation error for a field
   */
  addError(fieldName: string, error: string): void {
    if (!this.errors.has(fieldName)) {
      this.errors.set(fieldName, []);
    }
    this.errors.get(fieldName)!.push(error);
  }

  /**
   * Check if there are any validation errors
   */
  hasErrors(): boolean {
    return this.errors.size > 0;
  }

  /**
   * Get all errors as an object
   */
  getErrorsAsObject(): Record<string, string[]> {
    const result: Record<string, string[]> = {};
    for (const [key, value] of this.errors) {
      result[key] = value;
    }
    return result;
  }

  /**
   * Get all errors as an array with field names
   */
  getErrorsAsArray(): { field: string; message: string }[] {
    const result: { field: string; message: string }[] = [];
    for (const [field, errors] of this.errors) {
      for (const error of errors) {
        result.push({ field, message: error });
      }
    }
    return result;
  }

  /**
   * Get sanitized form data
   */
  getSanitizedData(): Record<string, any> {
    return this.sanitizedData;
  }

  /**
   * Get specific field errors
   */
  getFieldErrors(fieldName: string): string[] {
    return this.errors.get(fieldName) || [];
  }

  /**
   * Check if specific field has errors
   */
  hasFieldError(fieldName: string): boolean {
    return this.errors.has(fieldName);
  }
}

// ============================================================================
// PREDEFINED SCHEMAS (Common patterns)
// ============================================================================

/**
 * Schema for creating/updating a task
 */
export const createTaskSchema = (): FormSchema => {
  return FormSchema.create('CreateTaskSchema', {
    title: new FormField('title', 'title', true, 'Task Title', {
      minLength: 2,
      maxLength: 255,
    }),
    description: new FormField('description', 'body', false, 'Description', {
      minLength: 0,
      maxLength: 5000,
    }),
    priority: new FormField('priority', 'dropdown', false, 'Priority', {
      choices: ['low', 'medium', 'high', 'critical'],
    }),
    status: new FormField('status', 'dropdown', false, 'Status', {
      choices: ['todo', 'in_progress', 'completed', 'archived'],
    }),
    assignedToUserId: new FormField('assignedToUserId', 'integer', false, 'Assigned To', {
      minValue: 1,
    }),
    dueDate: new FormField('dueDate', 'iso_datetime', false, 'Due Date'),
    isShared: new FormField('isShared', 'boolean', false, 'Shared'),
    sendReminder: new FormField('sendReminder', 'boolean', false, 'Send Reminder'),
  });
};

/**
 * Schema for contact forms
 */
export const createContactSchema = (): FormSchema => {
  return FormSchema.create('ContactSchema', {
    email: new FormField('email', 'email', true, 'Email'),
    phone: new FormField('phone', 'phone', false, 'Phone', {
      minLength: 0,
    }),
    subject: new FormField('subject', 'title', true, 'Subject', {
      minLength: 3,
      maxLength: 100,
    }),
    message: new FormField('message', 'body', true, 'Message', {
      minLength: 10,
      maxLength: 5000,
    }),
  });
};

/**
 * Schema for user profile updates
 */
export const createProfileSchema = (): FormSchema => {
  return FormSchema.create('ProfileSchema', {
    firstName: new FormField('firstName', 'text', true, 'First Name', {
      minLength: 1,
      maxLength: 100,
    }),
    lastName: new FormField('lastName', 'text', true, 'Last Name', {
      minLength: 1,
      maxLength: 100,
    }),
    email: new FormField('email', 'email', true, 'Email'),
    phone: new FormField('phone', 'phone', false, 'Phone'),
    bio: new FormField('bio', 'body', false, 'Bio', {
      maxLength: 1000,
    }),
  });
};

/**
 * Schema for creating an individual client (person)
 */
export const createClientSchema = (isIndividual: boolean = true): FormSchema => {
  if (isIndividual) {
    return FormSchema.create('CreateClientSchema', {
      firstName: new FormField('firstName', 'text', true, 'First Name', {
        minLength: 2,
        maxLength: 100,
      }),
      lastName: new FormField('lastName', 'text', true, 'Last Name', {
        minLength: 2,
        maxLength: 100,
      }),
      email: new FormField('email', 'email', true, 'Email'),
      phone: new FormField('phone', 'phone', false, 'Phone'),
      address: new FormField('address', 'text', false, 'Address', {
        maxLength: 255,
      }),
      notes: new FormField('notes', 'body', false, 'Notes', {
        maxLength: 1000,
      }),
    });
  }

  // Business / Organization client
  return FormSchema.create('CreateClientSchema', {
    companyName: new FormField('companyName', 'text', true, 'Company / Organization Name', {
      minLength: 2,
      maxLength: 255,
    }),
    contactFirstName: new FormField('contactFirstName', 'text', false, 'Contact Person First Name', {
      minLength: 2,
      maxLength: 100,
    }),
    contactLastName: new FormField('contactLastName', 'text', false, 'Contact Person Last Name', {
      minLength: 2,
      maxLength: 100,
    }),
    contactEmail: new FormField('contactEmail', 'email', false, 'Contact Person Email'),
    email: new FormField('email', 'email', false, 'Company Email'),
    phone: new FormField('phone', 'phone', false, 'Phone'),
    address: new FormField('address', 'text', false, 'Address', {
      maxLength: 255,
    }),
    notes: new FormField('notes', 'body', false, 'Notes', {
      maxLength: 1000,
    }),
  });
};

/**
 * Schema for creating a matter
 */
export const createMatterSchema = (): FormSchema => {
  return FormSchema.create('CreateMatterSchema', {
    matterName: new FormField('matterName', 'title', false, 'Matter Name', {
      minLength: 2,
      maxLength: 255,
    }),
    matterType: new FormField('matterType', 'dropdown', false, 'Matter Type', {
      choices: ['litigation', 'corporate', 'intellectual_property', 'other'],
    }),
    status: new FormField('status', 'dropdown', false, 'Status', {
      choices: ['open', 'closed', 'on_hold'],
    }),
    jurisdiction: new FormField('jurisdiction', 'text', false, 'Jurisdiction', {
      maxLength: 100,
    }),
    description: new FormField('description', 'body', false, 'Description', {
      maxLength: 5000,
    }),
  });
};

/**
 * Schema for folder creation
 */
export const createFolderSchema = (): FormSchema => {
  return FormSchema.create('CreateFolderSchema', {
    folderName: new FormField('folderName', 'title', true, 'Folder Name', {
      minLength: 1,
      maxLength: 255,
    }),
  });
};

/**
 * Schema for creating a support ticket
 */
export const createSupportTicketSchema = (): FormSchema => {
  return FormSchema.create('CreateSupportTicketSchema', {
    title: new FormField('title', 'title', true, 'Ticket Title', {
      minLength: 3,
      maxLength: 255,
    }),
    description: new FormField('description', 'body', true, 'Description', {
      minLength: 10,
      maxLength: 5000,
    }),
    priority: new FormField('priority', 'dropdown', true, 'Priority', {
      choices: ['low', 'medium', 'high', 'critical'],
    }),
  });
};

/**
 * Quick validation function for common use case
 */
export const validateForm = (
  schema: FormSchema,
  formData: Record<string, any>,
  strict: boolean = false
): {
  isValid: boolean;
  errors: Record<string, string[]>;
  sanitizedData: Record<string, any>;
} => {
  const validator = new FormValidator(schema);
  return validator.validate(formData, strict);
};
